import type { VehicleDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { ActionMenu, EmptyState, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * What the household owns, and what each one costs.
 *
 * ==========================================================================================
 * TWO COST FIGURES, SIDE BY SIDE, NEVER SUMMED (FR-019)
 * ==========================================================================================
 *
 * **Cash per month** is money that leaves the household — the linked recurring costs. **Lost value
 * per month** is a balance changing, and no money moved.
 *
 * 019's amendment is the precedent, in the other direction: counting mortgage principal as a cost
 * "applied a fact about a BALANCE to a figure that is a FLOW, which is the one thing this page is
 * built not to do." Adding these two together would be the same error with a better disguise,
 * because depreciation genuinely IS the largest cost of owning a car and still is not cash.
 *
 * ==========================================================================================
 * NO DEPRECIATION FIGURE IS NOT €0 (FR-017)
 * ==========================================================================================
 *
 * Below two observations there is nothing to measure, and the row says so in words. Zero would claim
 * the vehicle held its value exactly; absence says nobody has measured it twice. `dashboard.md`'s
 * standard for an unpriced holding — omitted, never zeroed.
 *
 * ==========================================================================================
 * THE NAME OPENS THE VEHICLE; THE REST OF THE ROW'S ACTIONS SIT BEHIND ONE MENU
 * ==========================================================================================
 *
 * Both were reported. The name used to OPEN THE VALUATION DIALOG, which is a control disguised as a
 * link: the one thing a member expects clicking a row's subject to do is show them the subject.
 * There is a detail page now, and the name goes there — where the costs are, where the history is,
 * and where "record a value" is one of the actions rather than the only one.
 *
 * The remaining actions were two underlined text buttons in the last column, which is the exact
 * arrangement 019 removed from the expenses table on 2026-08-14: "two text buttons made the column
 * as wide as 'Edit' plus 'Restore' and put the row's controls in competition with its figures".
 * `ActionMenu` is what income's sources, a property's mortgage parts, a stock and every entered
 * expense already use.
 *
 * A SOLD VEHICLE STILL OFFERS NO MENU AT ALL — not a menu of disabled items. A greyed control
 * invites a member to work out why it will not respond, where an absent one says the state. The same
 * rule a mortgage row follows on the expenses page (FR-072).
 */
/**
 * A signed euro figure, with an explicit `+` on a gain.
 *
 * `formatEur` already prints the minus, and a gain with no sign reads as a plain amount rather than
 * as movement — "€2.000,00 since you bought it" could be the price. The plus is what makes the two
 * directions symmetrical at a glance, which matters because losing is the common case and gaining is
 * the one a reader will not be expecting.
 */
function signedEur(amountInEuros: number): string {
  return amountInEuros > 0 ? `+${formatEur(amountInEuros)}` : formatEur(amountInEuros);
}

export function VehicleList({
  vehicles,
  onOpen,
  onValue,
  onDispose,
}: {
  vehicles: readonly VehicleDto[];
  /** Open the vehicle's own page. */
  onOpen?: (vehicle: VehicleDto) => void;
  /** Record what it is worth. */
  onValue?: (vehicle: VehicleDto) => void;
  /** Record that it was sold. A SEPARATE action from archiving, which this list does not offer. */
  onDispose?: (vehicle: VehicleDto) => void;
}): ReactNode {
  const t = useT();

  if (vehicles.length === 0) {
    return <EmptyState>{t("veh.list.empty")}</EmptyState>;
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("veh.list.colVehicle")}</Table.HeaderCell>
          <Table.HeaderCell>{t("veh.list.colOwner")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("veh.list.colValue")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("veh.list.colCash")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("veh.list.colLostValue")}</Table.HeaderCell>
          <Table.HeaderCell numeric>
            <span className="sr-only">{t("veh.list.actions")}</span>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {vehicles.map((vehicle) => (
          <Table.Row key={vehicle.id}>
            <Table.Cell>
              {onOpen === undefined ? (
                <span className="font-medium">{vehicle.name}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onOpen(vehicle);
                  }}
                  className="rounded-control font-medium underline decoration-dotted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  {vehicle.name}
                </button>
              )}
              <div className="text-xs text-muted">
                {t(`veh.type.${vehicle.type}`)}
                {vehicle.linkedCostCount > 0
                  ? ` · ${t("veh.list.costs", { count: vehicle.linkedCostCount })}`
                  : ""}
              </div>
            </Table.Cell>
            <Table.Cell>
              {vehicle.owner.displayName ?? (
                <span className="text-muted">{t("veh.list.joint")}</span>
              )}
            </Table.Cell>
            <Table.Cell numeric>
              {vehicle.currentValueInEuros === null ? (
                // Never valued: contributes nothing to net worth, and says so rather than showing €0.
                <span className="text-muted">{t("veh.list.notValued")}</span>
              ) : (
                <>
                  {formatEur(vehicle.currentValueInEuros)}
                  {/*
                    WHAT IT HAS DONE SINCE THE HOUSEHOLD BOUGHT IT, under the figure it qualifies
                    rather than in a column of its own.

                    A sub-line and not a seventh column: this table already carries five figures, and
                    the gain is only ever read in relation to the one directly above it. `Change` on
                    the detail page is the same number given room.

                    NOT COLOURED. A car losing value is the ordinary case and the expected one —
                    painting it red would flag every healthy row as a problem, which is the argument
                    `dashboard.md` makes for the ring's arcs and the funnel's bands.
                  */}
                  {vehicle.gain === null ? null : (
                    <div className="text-xs text-muted">
                      {t("veh.list.sincePurchase", {
                        amount: signedEur(vehicle.gain.amountInEuros),
                      })}
                    </div>
                  )}
                  {/* A stale observation is flagged where the figure is, not in a banner elsewhere. */}
                  {vehicle.valuationIsStale ? (
                    <div className="text-xs text-muted">{t("veh.list.stale")}</div>
                  ) : null}
                </>
              )}
            </Table.Cell>
            <Table.Cell numeric>{formatEur(vehicle.monthlyCostInEuros)}</Table.Cell>
            <Table.Cell numeric>
              {vehicle.depreciation === null ? (
                <span className="text-muted">{t("veh.list.needsSecondValuation")}</span>
              ) : (
                formatEur(vehicle.depreciation.perMonthInEuros)
              )}
            </Table.Cell>
            <Table.Cell numeric>
              {vehicle.disposal === null ? (
                <div className="flex justify-end">
                  <ActionMenu
                    label={t("veh.list.actionsFor", { name: vehicle.name })}
                    items={[
                      ...(onOpen === undefined
                        ? []
                        : [
                            {
                              label: t("veh.list.open"),
                              onSelect: () => {
                                onOpen(vehicle);
                              },
                            },
                          ]),
                      ...(onValue === undefined
                        ? []
                        : [
                            {
                              label: t("veh.list.recordValue"),
                              onSelect: () => {
                                onValue(vehicle);
                              },
                            },
                          ]),
                      ...(onDispose === undefined
                        ? []
                        : [
                            {
                              label: t("veh.list.sell"),
                              onSelect: () => {
                                onDispose(vehicle);
                              },
                            },
                          ]),
                    ]}
                  />
                </div>
              ) : (
                <span className="text-xs text-muted">
                  {t("veh.list.sold", { amount: formatEur(vehicle.disposal.priceInEuros) })}
                </span>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
